import React from 'react';
import Icon from '../AppIcon';

const Button = React.forwardRef(({
    children,
    variant = 'primary',
    size = 'md',
    shape = 'rounded',
    fullWidth = false,
    disabled = false,
    loading = false,
    icon = null,
    iconName = null,
    iconPosition = 'left',
    type = 'button',
    iconSize = null,
    iconColor = null,
    justify = 'center',
    className = '',
    onClick,
    ...rest
}, ref) => {
    // Base classes with dynamic justify
    const justifyClasses = {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
        between: 'justify-between',
        around: 'justify-around',
        evenly: 'justify-evenly'
    };
    
    const baseClasses = `inline-flex items-center ${justifyClasses[justify] || justifyClasses.center} transition-all duration-200 font-medium focus:ring-2 focus:outline-none`;

    // Size classes with mobile-optimized touch targets
    const sizeClasses = {
        '2xs': 'text-xs py-0.5 px-1.5 min-h-[32px]',
        xs: 'text-xs py-1 px-2 min-h-[36px]',
        sm: 'text-sm py-1.5 px-3 min-h-[40px]',
        md: 'text-base py-2 px-4 min-h-[44px]', // iOS/Android minimum touch target
        lg: 'text-lg py-2.5 px-5 min-h-[48px]',
        xl: 'text-xl py-3 px-6 min-h-[52px]',
        '2xl': 'text-2xl py-4 px-8 min-h-[56px]',
    };

    // Shape classes
    const shapeClasses = {
        rounded: 'rounded',
        square: 'rounded-none',
        pill: 'rounded-full',
        circle: 'rounded-full aspect-square',
    };

    // TweakCN variant classes
    const variantClasses = {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-ring shadow-sm',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 focus:ring-ring shadow-sm',
        success: 'bg-success text-white hover:bg-success/90 focus:ring-green-500 shadow-sm',
        danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:ring-red-500 shadow-sm',
        warning: 'bg-warning text-white hover:bg-warning/90 focus:ring-orange-500 shadow-sm',
        info: 'bg-accent text-accent-foreground hover:bg-accent/80 focus:ring-ring shadow-sm',
        ghost: 'hover:bg-accent hover:text-accent-foreground focus:ring-ring',
        link: 'text-primary underline-offset-4 hover:underline focus:ring-ring p-0',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground focus:ring-ring shadow-sm',
        muted: 'bg-muted text-muted-foreground hover:bg-muted/80 focus:ring-ring shadow-sm',
    };


    // Width classes
    const widthClasses = fullWidth ? 'w-full' : '';

    // Disabled classes
    const disabledClasses = disabled ? 'cursor-not-allowed opacity-60' : '';

    // Loading state
    const loadingContent = loading ? (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
    ) : null;

    // Icon rendering
    const renderIcon = () => {
        if (iconName) {
            // Use AppIcon component when iconName is provided
            const iconSizeMap = {
                '2xs': 12,
                xs: 14,
                sm: 16,
                md: 18,
                lg: 20,
                xl: 22,
                '2xl': 24,
            };

            const calculatedSize = iconSize || iconSizeMap[size] || 18;

            return (
                <span style={{ color: iconColor || 'currentColor' }}>
                    <Icon
                        name={iconName}
                        size={calculatedSize}
                        className={`${children ? (iconPosition === 'left' ? 'mr-2' : 'ml-2') : ''}`}
                    />
                </span>

            );
        }

        if (!icon) return null;

        return React.cloneElement(icon, {
            className: `${children ? (iconPosition === 'left' ? 'mr-2' : 'ml-2') : ''} h-5 w-5`
        });
    };

    // Combine all classes
    const classes = `
                    ${baseClasses}
                    ${sizeClasses[size] || sizeClasses.md}
                    ${shapeClasses[shape] || shapeClasses.rounded}
                    ${variantClasses[variant] || variantClasses.primary}
                    ${widthClasses}
                    ${disabledClasses}
                    ${className}
                    `;

    return (
        <button
            ref={ref}
            type={type}
            className={classes}
            disabled={disabled || loading}
            onClick={onClick}
            {...rest}
        >
            {loading && loadingContent}
            {(icon || iconName) && iconPosition === 'left' && renderIcon()}
            {children}
            {(icon || iconName) && iconPosition === 'right' && renderIcon()}
        </button>
    );
});

export default Button;